import { useEffect } from "react";

/**
 * SEO / document head manager.
 *
 * The head is updated imperatively in the browser. This reliably
 * overwrites the static defaults that live in index.html (title, description,
 * og:*, twitter:*) without creating duplicate tags, and works the same in every
 * browser regardless of React-metadata hoisting nuances.
 *
 * index.html starts with window.prerenderReady=false. The existing prerender
 * service waits until this component has installed the final metadata and then
 * snapshots the page. Async pages therefore cannot be cached with the generic
 * fallback metadata by accident.
 */

export const SITE_NAME = "Светлячки";
export const BASE_URL = "https://fireflies.space";
export const DEFAULT_OG_IMAGE = `${BASE_URL}/images/og-cover.jpg`;
export const DEFAULT_DESCRIPTION =
  "Расскажи свою историю о любимых лапках и хвостиках — мы рядом, поддержим и обнимем.";

export interface SeoProps {
  /** Full <title> for the page. */
  title: string;
  /** Meta description. */
  description?: string;
  /**
   * Canonical path (e.g. "/about-us"). Defaults to the current pathname.
   * Always resolved to an absolute URL against BASE_URL.
   */
  canonicalPath?: string;
  /** Absolute or root-relative OG/Twitter image URL. */
  image?: string;
  /** Open Graph type. */
  type?: "website" | "article" | "profile";
  /** Set robots to noindex,nofollow for non-public pages. */
  noindex?: boolean;
  /** HTTP status returned by the prerender service (for example, 404). */
  statusCode?: number;
}

function toAbsolute(value: string): string {
  if (/^https?:\/\//i.test(value)) return value;
  return `${BASE_URL}${value.startsWith("/") ? "" : "/"}${value}`;
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr: "name" | "property", key: string) {
  document.head
    .querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
    ?.remove();
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function Seo({
  title,
  description,
  canonicalPath,
  image,
  type = "website",
  noindex = false,
  statusCode,
}: SeoProps) {
  useEffect(() => {
    const url = toAbsolute(canonicalPath ?? window.location.pathname);
    const img = toAbsolute(image || DEFAULT_OG_IMAGE);
    const pageDescription = description || DEFAULT_DESCRIPTION;

    document.title = title;
    setMeta("name", "description", pageDescription);

    setLink("canonical", url);

    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", "ru_RU");
    setMeta("property", "og:title", title);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("property", "og:image:secure_url", img);
    setMeta("property", "og:image:alt", title);
    setMeta("property", "og:description", pageDescription);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:image", img);
    setMeta("name", "twitter:image:alt", title);
    setMeta("name", "twitter:description", pageDescription);

    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    if (statusCode) {
      setMeta("name", "prerender-status-code", String(statusCode));
    } else {
      removeMeta("name", "prerender-status-code");
    }

    // Prerender checks this flag and captures the document only after all
    // route-specific metadata above is in the DOM.
    window.prerenderReady = true;
  }, [
    title,
    description,
    canonicalPath,
    image,
    type,
    noindex,
    statusCode,
  ]);

  return null;
}

export default Seo;
